import { useMemo } from "react";

import { X } from "lucide-react";

import { Button } from "@/modern/components/ui/button-shadcn";

import { useFileDetailModal } from "../../context/FileDetailModalContext";
import { ExtractionOperationList } from "./ExtractionOperationList";
import { ExtractionResultsView } from "./ExtractionResultsView";
import { DateExtractionResults } from "./results/DateExtractionResults";
import { SummaryExtractionResults } from "./results/SummaryExtractionResults";
import { ChecklistExtractionResults } from "./results/ChecklistExtractionResults";
import { LocationExtractionResults } from "./results/LocationExtractionResults";
import { BudgetExtractionResults } from "./results/BudgetExtractionResults";
import { ClientExtractionResults } from "./results/ClientExtractionResults";
import { NotesExtractionResults } from "./results/NotesExtractionResults";
import {
  ExtractionType,
  ExtractDatesResponse,
  ExtractSummaryResponse,
  ExtractChecklistResponse,
  ExtractLocationResponse,
  ExtractBudgetResponse,
  ExtractClientResponse,
  ExtractNotesResponse,
} from "./types";

/**
 * Computes the number of result items based on the extraction type and response data.
 */
const getResultCount = (type: ExtractionType | null, results: any): number | undefined => {
  if (!type || !results) return undefined;

  switch (type) {
    case "dates":
      return (results as ExtractDatesResponse).date_cards?.length;
    case "summary":
      return results.summary ? 1 : 0;
    case "checklist":
      return (results as ExtractChecklistResponse).checklists?.length;
    case "location":
      return (results as ExtractLocationResponse).locations?.length;
    case "budget":
      return (results as ExtractBudgetResponse).budgets?.length;
    case "client":
      return (results as ExtractClientResponse).clients?.length;
    case "notes":
      return (results as ExtractNotesResponse).notes?.length;
    default:
      return undefined;
  }
};

export const ExtractionPanel = () => {
  const {
    activeExtraction,
    extractionResults,
    extractionLoading,
    extractionError,
    cachedExtractionResults,
    handleRunExtraction,
    handleViewExtraction,
    handleCloseExtractionPanel,
    handleBackToOperationList,
    loadingPhase,
  } = useFileDetailModal();

  const resultCount = useMemo(
    () => getResultCount(activeExtraction, extractionResults),
    [activeExtraction, extractionResults]
  );

  const handleSelectOperation = (type: ExtractionType) => {
    handleViewExtraction(type);
  };

  const handleReExtract = () => {
    if (activeExtraction) {
      handleRunExtraction(activeExtraction);
    }
  };

  // State A: Operation selection list
  if (!activeExtraction) {
    return (
      <div className="flex flex-col h-full min-h-0 bg-card">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border shrink-0">
          <span className="text-sm font-medium text-foreground">Document Extraction</span>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={handleCloseExtractionPanel}
            aria-label="Close extraction panel"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          <ExtractionOperationList
            onSelectOperation={handleSelectOperation}
            cachedResults={cachedExtractionResults}
          />
        </div>
      </div>
    );
  }

  // State B: Results view
  const isCached = !extractionLoading && !extractionError && !!cachedExtractionResults[activeExtraction];

  return (
    <div className="flex flex-col h-full min-h-0 bg-card">
      <ExtractionResultsView
        type={activeExtraction}
        loading={extractionLoading}
        error={extractionError}
        onBack={handleBackToOperationList}
        onRetry={handleReExtract}
        onReExtract={isCached ? handleReExtract : undefined}
        resultCount={resultCount}
        loadingPhase={loadingPhase}
      >
        {activeExtraction === "dates" && extractionResults && (
          <DateExtractionResults
            dateCards={(extractionResults as ExtractDatesResponse).date_cards || []}
          />
        )}

        {activeExtraction === "summary" && extractionResults && (
          <SummaryExtractionResults
            summary={(extractionResults as ExtractSummaryResponse).summary || ""}
          />
        )}

        {activeExtraction === "checklist" && extractionResults && (
          <ChecklistExtractionResults
            checklists={(extractionResults as ExtractChecklistResponse).checklists || []}
          />
        )}

        {activeExtraction === "location" && extractionResults && (
          <LocationExtractionResults
            locations={(extractionResults as ExtractLocationResponse).locations || []}
          />
        )}

        {activeExtraction === "budget" && extractionResults && (
          <BudgetExtractionResults
            budgets={(extractionResults as ExtractBudgetResponse).budgets || []}
          />
        )}

        {activeExtraction === "client" && extractionResults && (
          <ClientExtractionResults
            clients={(extractionResults as ExtractClientResponse).clients || []}
          />
        )}

        {activeExtraction === "notes" && extractionResults && (
          <NotesExtractionResults
            notes={(extractionResults as ExtractNotesResponse)?.notes || []}
          />
        )}
      </ExtractionResultsView>
    </div>
  );
};
